/**
 * TeamDetailPage
 *
 * Displays team detail with member breakdown (main + reserve).
 * Captain/VC also see captain actions: remove member, promote VC, promote reserve, send invite.
 *
 * Route: /campaign/:name/:campaignId/teams/:teamId
 */
import { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Spin, Button, Input, Modal, Select, message as antMessage } from 'antd';
import {
  TeamOutlined,
  CrownOutlined,
  StarOutlined,
  UserOutlined,
  ArrowLeftOutlined,
  SendOutlined,
} from '@ant-design/icons';
import { useAuth } from '../../hooks/useAuth';
import { getCampaignTeamDetail, getMyTeam } from '../../api/challenges';
import axios from 'axios';
import { authHeaders, backendUrl } from '../../api/http';

const ROLE_LABELS = {
  captain: { label: 'Đội trưởng', icon: <CrownOutlined style={{ color: '#faad14' }} />, color: '#faad14' },
  vice_captain: { label: 'Phó ĐT', icon: <StarOutlined style={{ color: '#1677ff' }} />, color: '#1677ff' },
  reserve: { label: 'Dự bị', icon: <UserOutlined style={{ color: '#8c8c8c' }} />, color: '#8c8c8c' },
  member: { label: 'Thành viên', icon: <UserOutlined style={{ color: '#52c41a' }} />, color: '#52c41a' },
};

const STATUS_COLORS = {
  active: '#52c41a',
  warning: '#faad14',
  inactive: '#fa8c16',
  dropout: '#cf1322',
  replaced: '#8c8c8c',
};

function formatDistance(meters) {
  if (!meters && meters !== 0) return '—';
  return `${(meters / 1000).toFixed(2)} km`;
}

// ── Captain action hooks (calls REST endpoints directly) ──────────────────────

async function apiRemoveMember(user, campaignId, teamId, memberId) {
  return axios.delete(
    `${backendUrl}/api/strava-provider/campaigns/${campaignId}/teams/${teamId}/members/${memberId}`,
    { headers: authHeaders(user) }
  );
}

async function apiPromoteViceCaptain(user, campaignId, teamId, memberId) {
  return axios.put(
    `${backendUrl}/api/strava-provider/campaigns/${campaignId}/teams/${teamId}/members/${memberId}/promote`,
    {},
    { headers: authHeaders(user) }
  );
}

async function apiPromoteReserve(user, campaignId, teamId, memberId) {
  return axios.put(
    `${backendUrl}/api/strava-provider/campaigns/${campaignId}/teams/${teamId}/members/${memberId}/promote-reserve`,
    {},
    { headers: authHeaders(user) }
  );
}

async function apiSendInvite(user, campaignId, teamId, targetUserId) {
  return axios.post(
    `${backendUrl}/api/strava-provider/campaigns/${campaignId}/teams/${teamId}/invites`,
    { user_id: targetUserId },
    { headers: authHeaders(user) }
  );
}

// ── MemberRow ─────────────────────────────────────────────────────────────────

function MemberRow({ member, myRole, campaignId, teamId, onAction }) {
  const { user } = useAuth();
  const isCaptainOrVC = myRole === 'captain' || myRole === 'vice_captain';
  const roleInfo = ROLE_LABELS[member.role] || { label: member.role, icon: <UserOutlined />, color: '#8c8c8c' };

  async function handleRemove() {
    const name = member.user?.fullname || 'thành viên này';
    Modal.confirm({
      title: `Xóa ${name} khỏi team?`,
      okText: 'Xóa',
      okType: 'danger',
      cancelText: 'Hủy',
      onOk: async () => {
        try {
          await apiRemoveMember(user, campaignId, teamId, member.membership_id);
          antMessage.success('Đã xóa thành viên');
          onAction();
        } catch (e) {
          antMessage.error(e?.response?.data?.error?.message || 'Xóa thất bại');
        }
      },
    });
  }

  async function handlePromoteVC() {
    try {
      await apiPromoteViceCaptain(user, campaignId, teamId, member.membership_id);
      antMessage.success('Đã bổ nhiệm Phó đội trưởng');
      onAction();
    } catch (e) {
      antMessage.error(e?.response?.data?.error?.message || 'Thao tác thất bại');
    }
  }

  async function handlePromoteReserve() {
    try {
      await apiPromoteReserve(user, campaignId, teamId, member.membership_id);
      antMessage.success('Đã nâng lên thành viên chính');
      onAction();
    } catch (e) {
      antMessage.error(e?.response?.data?.error?.message || 'Thao tác thất bại');
    }
  }

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '10px 0',
        borderBottom: '1px solid #f0f0f0',
      }}
    >
      {/* Avatar */}
      {member.user?.avatar ? (
        <img
          src={member.user.avatar}
          alt=""
          style={{ width: 36, height: 36, borderRadius: '50%', objectFit: 'cover' }}
        />
      ) : (
        <div
          style={{
            width: 36,
            height: 36,
            borderRadius: '50%',
            background: '#f0f0f0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#8c8c8c',
          }}
        >
          <UserOutlined />
        </div>
      )}

      {/* Name + role */}
      <div style={{ flex: 1 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {roleInfo.icon}
          <span style={{ fontWeight: 600, fontSize: 14 }}>
            {member.user?.fullname || `#${member.user?.id}`}
          </span>
        </div>
        <div style={{ fontSize: 12, color: '#8c8c8c' }}>
          {roleInfo.label}
          {' · '}
          <span style={{ color: STATUS_COLORS[member.status] || '#8c8c8c' }}>
            {member.status}
          </span>
        </div>
      </div>

      {/* Distance */}
      <div style={{ textAlign: 'right', minWidth: 72 }}>
        <div style={{ fontWeight: 600, fontSize: 14 }}>
          {formatDistance(member.individual_distance)}
        </div>
        <div style={{ fontSize: 11, color: '#8c8c8c' }}>đã chạy</div>
      </div>

      {/* Captain actions */}
      {isCaptainOrVC && member.role !== 'captain' && (
        <div style={{ display: 'flex', gap: 4 }}>
          {myRole === 'captain' && member.role === 'member' && (
            <button
              onClick={handlePromoteVC}
              title="Bổ nhiệm Phó ĐT"
              style={smallBtnStyle('#1677ff')}
            >
              <StarOutlined />
            </button>
          )}
          {member.role === 'reserve' && (
            <button
              onClick={handlePromoteReserve}
              title="Nâng lên chính thức"
              style={smallBtnStyle('#52c41a')}
            >
              ↑
            </button>
          )}
          <button
            onClick={handleRemove}
            title="Xóa khỏi team"
            style={smallBtnStyle('#cf1322')}
          >
            ✕
          </button>
        </div>
      )}
    </div>
  );
}

function smallBtnStyle(color) {
  return {
    width: 28,
    height: 28,
    borderRadius: 6,
    border: `1px solid ${color}`,
    background: 'transparent',
    color,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 14,
  };
}

// ── InviteModal ───────────────────────────────────────────────────────────────

function InviteModal({ open, onClose, user, campaignId, teamId }) {
  const [userId, setUserId] = useState('');
  const [sending, setSending] = useState(false);

  async function handleSend() {
    if (!userId.trim()) {
      antMessage.warning('Nhập User ID để mời');
      return;
    }
    setSending(true);
    try {
      await apiSendInvite(user, campaignId, teamId, parseInt(userId));
      antMessage.success('Đã gửi lời mời!');
      onClose();
    } catch (e) {
      antMessage.error(e?.response?.data?.error?.message || 'Gửi lời mời thất bại');
    } finally {
      setSending(false);
    }
  }

  return (
    <Modal
      title={<><SendOutlined /> Gửi lời mời tham gia team</>}
      open={open}
      onCancel={onClose}
      onOk={handleSend}
      okText="Gửi lời mời"
      confirmLoading={sending}
    >
      <p style={{ color: '#8c8c8c', marginBottom: 12, fontSize: 13 }}>
        Nhập User ID của người bạn muốn mời. Lời mời có hiệu lực trong 7 ngày.
      </p>
      <Input
        placeholder="User ID (số)"
        value={userId}
        onChange={(e) => setUserId(e.target.value)}
        type="number"
      />
    </Modal>
  );
}

// ── Main TeamDetailPage ───────────────────────────────────────────────────────

export default function TeamDetailPage() {
  const { campaignId, teamId, name } = useParams();
  const { user, isLoggedIn } = useAuth();

  const [team, setTeam] = useState(null);
  const [myTeam, setMyTeam] = useState(null);
  const [loading, setLoading] = useState(true);
  const [inviteOpen, setInviteOpen] = useState(false);

  const fetchTeam = useCallback(async () => {
    setLoading(true);
    try {
      const [detail, mine] = await Promise.all([
        getCampaignTeamDetail(campaignId, teamId),
        isLoggedIn ? getMyTeam(user, campaignId) : Promise.resolve(null),
      ]);
      setTeam(detail);
      setMyTeam(mine);
    } finally {
      setLoading(false);
    }
  }, [campaignId, teamId, user, isLoggedIn]);

  useEffect(() => { fetchTeam(); }, [fetchTeam]);

  if (loading) {
    return (
      <div style={{ textAlign: 'center', paddingTop: 60 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!team) {
    return (
      <div style={{ textAlign: 'center', paddingTop: 60 }}>
        <p>Không tìm thấy team.</p>
        <Link to={`/campaign/${name}/${campaignId}`}>← Quay lại campaign</Link>
      </div>
    );
  }

  // myRole in this team
  const isMyTeam = myTeam && String(myTeam.id) === String(teamId);
  const myRole = isMyTeam ? myTeam.my_role : null;
  const isCaptainOrVC = myRole === 'captain' || myRole === 'vice_captain';

  const mainMembers = team.main_members || [];
  const reserveMembers = team.reserve_members || [];
  const penalty = team.penalty_distance_total || 0;

  return (
    <div
      style={{
        maxWidth: 600,
        margin: '0 auto',
        padding: '16px 16px 80px',
      }}
    >
      {/* Back link */}
      <Link
        to={`/campaign/${name}/${campaignId}`}
        style={{ display: 'inline-flex', alignItems: 'center', gap: 4, marginBottom: 16, color: '#595959' }}
      >
        <ArrowLeftOutlined /> Quay lại campaign
      </Link>

      {/* Team header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 16,
          marginBottom: 24,
          padding: 16,
          background: '#fff',
          borderRadius: 12,
          boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
        }}
      >
        {team.logo_url ? (
          <img
            src={team.logo_url}
            alt={team.name}
            style={{ width: 64, height: 64, borderRadius: 10, objectFit: 'cover' }}
          />
        ) : (
          <div
            style={{
              width: 64,
              height: 64,
              borderRadius: 10,
              background: '#f0f0f0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <TeamOutlined style={{ fontSize: 28, color: '#8c8c8c' }} />
          </div>
        )}
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 700, fontSize: 18 }}>{team.name}</div>
          {team.slogan && (
            <div style={{ color: '#8c8c8c', fontSize: 13, marginTop: 2 }}>{team.slogan}</div>
          )}
          <div style={{ marginTop: 6, display: 'flex', gap: 12, fontSize: 13 }}>
            <span>
              <strong>{mainMembers.length}</strong> thành viên chính
            </span>
            {reserveMembers.length > 0 && (
              <span style={{ color: '#8c8c8c' }}>
                <strong>{reserveMembers.length}</strong> dự bị
              </span>
            )}
            {penalty > 0 && (
              <span style={{ color: '#cf1322' }}>
                Phạt: −{formatDistance(penalty)}
              </span>
            )}
          </div>
          {team.replacement_tokens_remaining !== undefined && (
            <div style={{ fontSize: 11, color: '#8c8c8c', marginTop: 2 }}>
              Tokens thay thế còn: {team.replacement_tokens_remaining}/2
            </div>
          )}
        </div>

        {/* Captain actions */}
        {isCaptainOrVC && (
          <Button
            icon={<SendOutlined />}
            size="small"
            onClick={() => setInviteOpen(true)}
          >
            Mời
          </Button>
        )}
      </div>

      {/* Main members */}
      <SectionHeader title="Thành viên chính" count={mainMembers.length} />
      <div
        style={{
          background: '#fff',
          borderRadius: 12,
          padding: '0 16px',
          marginBottom: 20,
          boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
        }}
      >
        {mainMembers.map((m) => (
          <MemberRow
            key={m.membership_id}
            member={m}
            myRole={myRole}
            campaignId={campaignId}
            teamId={teamId}
            onAction={fetchTeam}
          />
        ))}
        {mainMembers.length === 0 && (
          <div style={{ padding: '16px 0', color: '#8c8c8c', textAlign: 'center' }}>
            Chưa có thành viên chính.
          </div>
        )}
      </div>

      {/* Reserve members */}
      {reserveMembers.length > 0 && (
        <>
          <SectionHeader title="Thành viên dự bị" count={reserveMembers.length} />
          <div
            style={{
              background: '#fff',
              borderRadius: 12,
              padding: '0 16px',
              marginBottom: 20,
              boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
              opacity: 0.85,
            }}
          >
            {reserveMembers.map((m) => (
              <MemberRow
                key={m.membership_id}
                member={m}
                myRole={myRole}
                campaignId={campaignId}
                teamId={teamId}
                onAction={fetchTeam}
              />
            ))}
          </div>
        </>
      )}

      {/* Invite modal */}
      <InviteModal
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        user={user}
        campaignId={campaignId}
        teamId={teamId}
      />
    </div>
  );
}

function SectionHeader({ title, count }) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 8,
      }}
    >
      <span style={{ fontWeight: 600, fontSize: 15 }}>{title}</span>
      <span style={{ fontSize: 13, color: '#8c8c8c' }}>{count} người</span>
    </div>
  );
}
